import { Chain } from '@1delta/chain-registry'
import { getEvmClient } from '@1delta/providers'
import type { Address } from 'viem'

/**
 * Core chain (1116) validator discovery.
 *
 * stCORE (`Earn.mint(validator)`) and SCORE (`deposit(validator)`)
 * require the caller to choose a Core validator to delegate the staked
 * CORE to. Core exposes the set on-chain through two system precompiles:
 *
 *  - `ValidatorSet.getValidators()` — the currently *elected* set (these
 *    earn rewards in the active round).
 *  - `CandidateHub.getCandidates()` — every registered candidate
 *    (delegatable; a superset of the elected validators).
 *
 * Use an *elected* validator for the deposit builders' `options.validator`
 * — delegating to a non-elected candidate stakes fine but accrues no
 * reward until/unless it is elected.
 */

/** Core `ValidatorSet` system contract. */
const VALIDATOR_SET: Address = '0x0000000000000000000000000000000000001000'
/** Core `CandidateHub` system contract. */
const CANDIDATE_HUB: Address = '0x0000000000000000000000000000000000001005'

const GET_VALIDATORS_ABI = [
  {
    name: 'getValidators',
    type: 'function',
    stateMutability: 'view',
    inputs: [],
    outputs: [{ type: 'address[]' }],
  },
] as const

const GET_CANDIDATES_ABI = [
  {
    name: 'getCandidates',
    type: 'function',
    stateMutability: 'view',
    inputs: [],
    outputs: [{ type: 'address[]' }],
  },
] as const

export interface CoreValidators {
  /** Currently-elected validators — the set earning rewards this round.
   *  Prefer these for `options.validator` on stCORE / SCORE mints. */
  validators: Address[]
  /** All registered candidates (delegatable superset of `validators`). */
  candidates: Address[]
}

/**
 * Fetch the live Core validator + candidate operator addresses on-chain.
 *
 * Rotates through the configured Core RPCs (`maxRpcTries`) so a single
 * flaky endpoint doesn't fail the read. Throws only if every RPC fails.
 */
export const getCoreValidators = async (
  maxRpcTries = 4,
): Promise<CoreValidators> => {
  let lastErr: unknown
  for (let rpcId = 0; rpcId < maxRpcTries; rpcId++) {
    try {
      const client = getEvmClient(Chain.CORE_BLOCKCHAIN_MAINNET, rpcId)
      const [validators, candidates] = await Promise.all([
        client.readContract({
          address: VALIDATOR_SET,
          abi: GET_VALIDATORS_ABI,
          functionName: 'getValidators',
        }) as Promise<readonly Address[]>,
        client.readContract({
          address: CANDIDATE_HUB,
          abi: GET_CANDIDATES_ABI,
          functionName: 'getCandidates',
        }) as Promise<readonly Address[]>,
      ])
      return {
        validators: [...validators],
        candidates: [...candidates],
      }
    } catch (e) {
      lastErr = e
    }
  }
  throw new Error(
    `getCoreValidators: all Core RPCs failed (${String(lastErr)})`,
  )
}
